<template>
  <VOnboardingWrapper ref="wrapper" :steps="steps">
    <template #default="{ previous, next, step, exit, isFirst, isLast, index }">
      <VOnboardingStep
        class="VOnboardingStep-wrapper"
        :step="index"
        :class="{ 'VOnboardingStep-center': step?.center }"
      >
        <div class="card shadow-lg secondary-300 m-auto" data-cy="parent-tour">
          <div class="card-body" :class="({ 'text-center': index == 0 }, step.class)">
            <h5 class="card-title" v-if="step.content.title">{{ step.content.title }}</h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="handleExit"
              data-cy="parent-tour-close"
            ></button>
            <figure v-if="step.content.image || step.content.imageComponent" class="img-fluid px-4 mt-3 mb-4">
              <component v-if="step.content.imageComponent" :is="step.content.imageComponent" class="card-image" alt="Welcome image" />
              <img v-else class="card-image" :src="step.content.image" alt="Welcome image" />
            </figure>
            <p class="card-text" v-if="step.content.description">{{ step.content.description }}</p>
          </div>
          <div class="card-footer navigation">
            <template v-if="isFirst">
              <button @click="next" type="button" class="btn btn-primary mx-auto">
                {{ step.content.cta }}
              </button>
            </template>
            <template v-else>
              <button @click="previous" type="button" class="btn btn-primary" v-t="'message.Tour.back'"></button>
              <button @click="next" type="button" class="btn btn-primary">
                {{ isLast ? $t('message.Tour.finish') : $t('message.Tour.continue') }}
              </button>
              <button v-if="step.content.error" disabled type="button" class="btn btn-primary">
                <i class="bi bi-exclamation-triangle-fill me-1 opacity-75"></i>
                {{ step.content.error }}
              </button>
            </template>
          </div>
        </div>
      </VOnboardingStep>
    </template>
  </VOnboardingWrapper>
</template>

<script>
import { defineComponent, ref } from "vue";
import TourListIcon from "@/assets/icons/tour-list.svg";

import { VOnboardingWrapper, VOnboardingStep, useVOnboarding } from "v-onboarding";

export default defineComponent({
  components: {
    VOnboardingWrapper,
    VOnboardingStep
  },
  emits: ['tour-closed'],
  setup(_, { emit }) {
    const wrapper = ref(null);
    const { start, goToStep, finish } = useVOnboarding(wrapper);
    let steps = [];
    const agendaSteps = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Welcome to PTC Wizard!",
          description: "It only takes about 30 seconds to learn what makes us the best parent-teacher conference app ever.",
          imageComponent: TourListIcon,
          cta: "Let's Begin"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "View your schedule the way you want",
          description:
            "When you sign in, you will see a page that lists your already-scheduled meetings. Choose between viewing all your scheduled meetings in a single list, or broken down by conference."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Let's schedule a meeting",
          description:
            "Simply find the conference you want and press here to start choosing a teacher and time to meet."
        }
      }
    ];
    const agendaStepsSpanish = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Bienvenido a  PTC Wizard!",
          description:"Solo toma alrededor de 30 segundos aprender qué nos convierte en la mejor aplicación de conferencias de padres y maestros de la historia.",
          imageComponent: TourListIcon,
          cta: "Empecemos"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Vea su horario de la manera que desee",
          description:
            "Cuando inicie sesión, verá una página que enumera sus reuniones ya programadas. Elija entre ver todas sus reuniones programadas en una sola lista o desglosadas por conferencia."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Programemos una reunión",
          description:
            "Simplemente busque la conferencia que desea y haga clic aquí para comenzar a elegir un maestro y la hora para reunirse."
        }
      }
    ];
    // // eslint-disable-next-line
    // const matrixDetailedSteps = [
    //   {
    //     attachTo: { element: ".navbar" },
    //     content: {
    //       title: "Sign up for meetings",
    //       description: "With PTC Wizard, booking meetings is as easy as 1-2-3.",
    //       image: "/tour-matrix.svg",
    //       cta: "Let's Do It"
    //     },
    //     center: true
    //   },
    //   {
    //     attachTo: { element: "#pills-tab" },
    //     content: {
    //       title: "Step 1: Choose the date",
    //       description: "All dates of the conference are shown here. Simply press the one you want."
    //     }
    //   },
    //   {
    //     attachTo: { element: "#BtnDrawerTeachers" },
    //     content: {
    //       title: "Step 2: Choose your teachers",
    //       description:
    //         "Press the TEACHERS button to see all available teachers and/or teams for that conference day. Press on any/all teachers to display their schedule.",
    //       error: "Choose a teacher"
    //     }
    //   },
    //   {
    //     attachTo: { element: "#matrix" },
    //     content: {
    //       title: "Step 3: Pick a timeslot",
    //       description:
    //         "This is where the magic happens. PTC Wizard shows you all your selected teachers' schedules together, and all you need to do is choose timeslots that work for you."
    //     },
    //     class: "primary-50",
    //     options: {
    //       scrollToStep: {
    //         enabled: false
    //       }
    //     },
    //     center: true
    //   },
    //   {
    //     attachTo: { element: ".btn-timeslot-available" },
    //     content: {
    //       title: "Choose Time",
    //       description:
    //         "Simply press on any time shown and your reservation is immediately saved. You can always change or cancel your meeting reservation. And to prevent double-booking, we update the available timeslots in real time."
    //     }
    //   },
    //   {
    //     attachTo: { element: "#collapse" },
    //     content: {
    //       title: "Fit more schedules on the screen",
    //       description:
    //         "Collapse or expand the width of columns in the table in order to see even more schedules at once."
    //     }
    //   },
    //   {
    //     attachTo: { element: ".handle" },
    //     content: {
    //       title: "Reorder teachers",
    //       description: "Drag columns left or right to make it easier to plan your schedule."
    //     }
    //   },
    //   {
    //     attachTo: { element: "#BtnDrawerAgenda" },
    //     content: {
    //       title: "Review your agenda",
    //       description:
    //         "Your meeting is booked as soon as you choose a timeslot, but you can review all your meetings at any time by opening the agenda drawer. From here you can also choose to send a copy of your full agenda to your email address."
    //     }
    //   }
    // ];
    const matrixSteps = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Sign up for meetings",
          description: "With PTC Wizard, booking meetings is as easy as 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Let's Do It"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Step 1: Choose the date",
          description: "All dates of the conference are shown here. Simply press the one you want."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Step 2: Choose your teachers",
          description:
            "Press the TEACHERS button to see all available teachers and/or teams for that conference day. Press on any/all teachers to display their schedule.",
          error: "Choose a teacher"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Step 3: Pick a timeslot",
          image: "/tour-matrix-4.png",
          description:
            "This is where the magic happens. PTC Wizard shows you all your selected teachers' schedules together, and all you need to do is choose timeslots that work for you."
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Review your agenda",
          image: "/tour-matrix-5.png",
          description:
            "Your meeting is booked as soon as you choose a timeslot, but you can review all your meetings at any time by opening the agenda drawer. From here you can also choose to send a copy of your full agenda to your email address by pressing the Confirm Schedule button."
        }
      }
    ];
    const matrixStepsSpanish = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Regístrese para las reuniones",
          description: "Con PTC Wizard, programar reuniones es tan fácil como 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Vamos a hacerlo"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Paso 1: Elige la fecha",
          description: "Todas las fechas de la conferencia se muestran aquí. Simplemente presione el que desee."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Paso 2: Elige a tus profesores",
          description:
            "Presione el botón PROFESORES para ver todos los profesores y/o equipos disponibles para ese día de conferencia. Haga clic en cualquiera/todos los profesores para mostrar su horario.",
          error: "Elige un profesor"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Paso 3: elige un intervalo de tiempo",
          image: "/tour-matrix-4.png",
          description:
            "Aquí es donde ocurre la magia. PTC Wizard le muestra todos los horarios de sus profesores seleccionados juntos, y todo lo que necesita hacer es elegir los intervalos de tiempo que le convengan."
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Revisa tu agenda",
          image: "/tour-matrix-5.png",
          description:
            "Su reunión se reserva tan pronto como elija un intervalo de tiempo, pero puede revisar todas sus reuniones en cualquier momento abriendo el cajón de la agenda. Desde aquí también puede optar por enviar una copia de su agenda completa a su dirección de correo electrónico presionando el botón Confirmar programa."
        }
      }
    ];

    // French tour steps
    const agendaStepsFrench = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Bienvenue sur PTC Wizard !",
          description: "Il ne faut que 30 secondes environ pour apprendre ce qui fait de nous la meilleure application de conférences parents-enseignants de tous les temps.",
          imageComponent: TourListIcon,
          cta: "Commençons"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Consultez votre emploi du temps comme vous le souhaitez",
          description: "Lorsque vous vous connectez, vous verrez une page qui répertorie vos réunions déjà programmées. Choisissez entre voir toutes vos réunions programmées dans une seule liste ou réparties par conférence."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Planifions une réunion",
          description: "Trouvez simplement la conférence que vous voulez et appuyez ici pour commencer à choisir un enseignant et une heure de rencontre."
        }
      }
    ];

    const matrixStepsFrench = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Inscrivez-vous aux réunions",
          description: "Avec PTC Wizard, réserver des réunions est aussi simple que 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Faisons-le"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Étape 1 : Choisissez la date",
          description: "Toutes les dates de la conférence sont affichées ici. Appuyez simplement sur celle que vous voulez."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Étape 2 : Choisissez vos enseignants",
          description: "Appuyez sur le bouton ENSEIGNANTS pour voir tous les enseignants et/ou équipes disponibles pour ce jour de conférence. Appuyez sur n'importe quel(s) enseignant(s) pour afficher leur emploi du temps.",
          error: "Choisissez un enseignant"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Étape 3 : Choisissez un créneau horaire",
          image: "/tour-matrix-4.png",
          description: "C'est ici que la magie opère. PTC Wizard vous montre tous les emplois du temps de vos enseignants sélectionnés ensemble, et tout ce que vous avez à faire est de choisir les créneaux horaires qui vous conviennent."
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Consultez votre agenda",
          image: "/tour-matrix-5.png",
          description: "Votre réunion est réservée dès que vous choisissez un créneau horaire, mais vous pouvez consulter toutes vos réunions à tout moment en ouvrant le tiroir d'agenda. À partir d'ici, vous pouvez également choisir d'envoyer une copie de votre agenda complet à votre adresse e-mail en appuyant sur le bouton Confirmer l'horaire."
        }
      }
    ];

    // German tour steps
    const agendaStepsGerman = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Willkommen bei PTC Wizard!",
          description: "Es dauert nur etwa 30 Sekunden zu lernen, was uns zur besten Eltern-Lehrer-Konferenz-App aller Zeiten macht.",
          imageComponent: TourListIcon,
          cta: "Lass uns beginnen"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Sehen Sie Ihren Zeitplan wie Sie möchten",
          description: "Wenn Sie sich anmelden, sehen Sie eine Seite, die Ihre bereits geplanten Besprechungen auflistet. Wählen Sie zwischen der Anzeige aller Ihrer geplanten Besprechungen in einer einzigen Liste oder aufgeteilt nach Konferenzen."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Lass uns ein Treffen planen",
          description: "Finden Sie einfach die gewünschte Konferenz und drücken Sie hier, um einen Lehrer und eine Zeit für das Treffen auszuwählen."
        }
      }
    ];

    const matrixStepsGerman = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Melden Sie sich für Besprechungen an",
          description: "Mit PTC Wizard ist das Buchen von Besprechungen so einfach wie 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Lass es uns machen"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Schritt 1: Wählen Sie das Datum",
          description: "Alle Daten der Konferenz werden hier angezeigt. Drücken Sie einfach das gewünschte."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Schritt 2: Wählen Sie Ihre Lehrer",
          description: "Drücken Sie die LEHRER-Taste, um alle verfügbaren Lehrer und/oder Teams für diesen Konferenztag zu sehen. Drücken Sie auf beliebige Lehrer, um ihren Zeitplan anzuzeigen.",
          error: "Wählen Sie einen Lehrer"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Schritt 3: Wählen Sie einen Zeitschlitz",
          image: "/tour-matrix-4.png",
          description: "Hier passiert die Magie. PTC Wizard zeigt Ihnen alle Zeitpläne Ihrer ausgewählten Lehrer zusammen, und alles was Sie tun müssen ist, Zeitschlitze zu wählen, die für Sie funktionieren."
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Überprüfen Sie Ihre Agenda",
          image: "/tour-matrix-5.png",
          description: "Ihr Meeting wird gebucht, sobald Sie einen Zeitschlitz wählen, aber Sie können alle Ihre Meetings jederzeit überprüfen, indem Sie die Agenda-Schublade öffnen. Von hier aus können Sie auch wählen, eine Kopie Ihrer vollständigen Agenda an Ihre E-Mail-Adresse zu senden, indem Sie die Schaltfläche Zeitplan bestätigen drücken."
        }
      }
    ];

    // Arabic tour steps
    const agendaStepsArabic = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "مرحبًا بك في PTC Wizard!",
          description: "يستغرق الأمر حوالي 30 ثانية فقط لتعلم ما يجعلنا أفضل تطبيق لمؤتمرات أولياء الأمور والمعلمين على الإطلاق.",
          imageComponent: TourListIcon,
          cta: "لنبدأ"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "اعرض جدولك الزمني بالطريقة التي تريدها",
          description: "عندما تسجل الدخول، ستشاهد صفحة تعرض اجتماعاتك المجدولة مسبقًا. اختر بين عرض جميع اجتماعاتك المجدولة في قائمة واحدة، أو مقسمة حسب المؤتمر."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "لنجدول اجتماعًا",
          description: "ابحث ببساطة عن المؤتمر الذي تريده واضغط هنا لبدء اختيار معلم ووقت للقاء."
        }
      }
    ];

    const matrixStepsArabic = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "سجل للاجتماعات",
          description: "مع PTC Wizard، حجز الاجتماعات سهل مثل 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "لنفعل ذلك"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "الخطوة 1: اختر التاريخ",
          description: "جميع تواريخ المؤتمر معروضة هنا. اضغط ببساطة على التاريخ الذي تريده."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "الخطوة 2: اختر معلميك",
          description: "اضغط على زر المعلمين لرؤية جميع المعلمين و/أو الفرق المتاحة لذلك اليوم من المؤتمر. اضغط على أي/جميع المعلمين لعرض جدولهم الزمني.",
          error: "اختر معلمًا"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "الخطوة 3: اختر فترة زمنية",
          image: "/tour-matrix-4.png",
          description: "هنا يحدث السحر. يعرض لك PTC Wizard جميع جداول معلميك المختارين معًا، وكل ما عليك فعله هو اختيار الفترات الزمنية التي تناسبك."
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "راجع جدول أعمالك",
          image: "/tour-matrix-5.png",
          description: "يتم حجز اجتماعك بمجرد اختيار فترة زمنية، ولكن يمكنك مراجعة جميع اجتماعاتك في أي وقت عن طريق فتح درج جدول الأعمال. من هنا يمكنك أيضًا اختيار إرسال نسخة من جدول أعمالك الكامل إلى عنوان بريدك الإلكتروني بالضغط على زر تأكيد الجدول الزمني."
        }
      }
    ];

    // Chinese tour steps
    const agendaStepsChinese = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "欢迎使用 PTC Wizard！",
          description: "只需要大约30秒的时间就能了解为什么我们是史上最好的家长-教师会议应用程序。",
          imageComponent: TourListIcon,
          cta: "让我们开始"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "按您想要的方式查看您的日程",
          description: "当您登录时，您将看到一个列出您已安排会议的页面。选择在单个列表中查看所有已安排的会议，或按会议分类查看。"
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "让我们安排一次会议",
          description: "只需找到您想要的会议并在此处按下以开始选择教师和会面时间。"
        }
      }
    ];

    const matrixStepsChinese = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "注册会议",
          description: "使用 PTC Wizard，预订会议就像 1-2-3 一样简单。",
          image: "/tour-matrix.svg",
          cta: "让我们开始"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "第1步：选择日期",
          description: "会议的所有日期都显示在这里。只需按下您想要的日期。"
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "第2步：选择您的教师",
          description: "按下教师按钮查看该会议日的所有可用教师和/或团队。按下任何/所有教师来显示他们的时间表。",
          error: "选择一位教师"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "第3步：选择时间段",
          image: "/tour-matrix-4.png",
          description: "这就是魔法发生的地方。PTC Wizard 将您选择的所有教师的时间表一起显示，您只需要选择适合您的时间段。"
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "查看您的议程",
          image: "/tour-matrix-5.png",
          description: "一旦您选择时间段，您的会议就会被预订，但您可以随时通过打开议程抽屉查看所有会议。从这里您还可以选择通过按下确认时间表按钮将您的完整议程副本发送到您的电子邮件地址。"
        }
      }
    ];

    // Japanese tour steps
    const agendaStepsJapanese = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "PTC Wizardへようこそ！",
          description: "私たちが史上最高の保護者と教師の会議アプリである理由を学ぶのに約30秒しかかかりません。",
          imageComponent: TourListIcon,
          cta: "始めましょう"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "お好みの方法でスケジュールを表示",
          description: "サインインすると、すでにスケジュールされた会議を一覧表示するページが表示されます。すべてのスケジュールされた会議を単一のリストで表示するか、会議別に分類して表示するかを選択してください。"
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "会議をスケジュールしましょう",
          description: "希望する会議を見つけて、ここを押して教師と会う時間を選択し始めてください。"
        }
      }
    ];

    // Bengali tour steps
    const agendaStepsBengali = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "PTC Wizard-এ স্বাগতম!",
          description: "আমরা কেন সর্বকালের সেরা অভিভাবক-শিক্ষক কনফারেন্স অ্যাপ তা জানতে মাত্র ৩০ সেকেন্ড সময় লাগে।",
          imageComponent: TourListIcon,
          cta: "শুরু করি"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "আপনার পছন্দ মতো সময়সূচী দেখুন",
          description: "যখন আপনি সাইন ইন করবেন, আপনি একটি পৃষ্ঠা দেখতে পাবেন যা আপনার ইতিমধ্যে নির্ধারিত মিটিংগুলি তালিকাভুক্ত করে। আপনার সমস্ত নির্ধারিত মিটিং একটি তালিকায় দেখা বা কনফারেন্স অনুযায়ী ভাগ করে দেখার মধ্যে বেছে নিন।"
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "চলুন একটি মিটিং নির্ধারণ করি",
          description: "আপনি যে কনফারেন্সটি চান তা খুঁজে নিন এবং একজন শিক্ষক ও মিলিত হওয়ার সময় বেছে নিতে এখানে চাপুন।"
        }
      }
    ];

    const matrixStepsJapanese = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "会議に登録",
          description: "PTC Wizardでは、会議の予約は1-2-3のように簡単です。",
          image: "/tour-matrix.svg",
          cta: "やってみましょう"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "ステップ1：日付を選択",
          description: "会議のすべての日付がここに表示されます。希望するものを押すだけです。"
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "ステップ2：教師を選択",
          description: "教師ボタンを押して、その会議日に利用可能なすべての教師やチームを表示します。任意/すべての教師を押してスケジュールを表示してください。",
          error: "教師を選択してください"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "ステップ3：時間枠を選択",
          image: "/tour-matrix-4.png",
          description: "ここで魔法が起こります。PTC Wizardは選択した教師のスケジュールをすべて一緒に表示し、あなたに合う時間枠を選択するだけです。"
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "議題を確認",
          image: "/tour-matrix-5.png",
          description: "時間枠を選択するとすぐに会議が予約されますが、議題ドロワーを開いていつでもすべての会議を確認できます。ここから、スケジュール確認ボタンを押して完全な議題のコピーをメールアドレスに送信することもできます。"
        }
      }
    ];

    const matrixStepsBengali = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "মিটিংগুলির জন্য নিবন্ধন করুন",
          description: "PTC Wizard দিয়ে, মিটিং বুক করা ১-২-৩ এর মতোই সহজ।",
          image: "/tour-matrix.svg",
          cta: "চলুন করি"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "ধাপ ১: তারিখ বেছে নিন",
          description: "কনফারেন্সের সমস্ত তারিখ এখানে দেখানো হয়েছে। আপনি যেটি চান সেটিতে চাপুন।"
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "ধাপ ২: আপনার শিক্ষকদের বেছে নিন",
          description: "সেই কনফারেন্স দিনের জন্য উপলব্ধ সমস্ত শিক্ষক এবং/অথবা টিম দেখতে শিক্ষক বাটনে চাপুন। তাদের সময়সূচী প্রদর্শনের জন্য যেকোনো/সমস্ত শিক্ষকের উপর চাপুন।",
          error: "একজন শিক্ষক বেছে নিন"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "ধাপ ৩: একটি সময়কাল বেছে নিন",
          image: "/tour-matrix-4.png",
          description: "এখানেই জাদু ঘটে। PTC Wizard আপনার নির্বাচিত সমস্ত শিক্ষকের সময়সূচী একসাথে দেখায়, এবং আপনার যা করতে হবে তা হল আপনার জন্য উপযুক্ত সময়কাল বেছে নেওয়া।"
        },
        options: {
          scrollToStep: {
            enabled: false
          }
        },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "আপনার এজেন্ডা পর্যালোচনা করুন",
          image: "/tour-matrix-5.png",
          description: "আপনি একটি সময়কাল বেছে নেওয়ার সাথে সাথেই আপনার মিটিং বুক হয়ে যায়, কিন্তু এজেন্ডা ড্রয়ার খুলে যেকোনো সময় আপনার সমস্ত মিটিং পর্যালোচনা করতে পারেন। এখান থেকে আপনি সময়সূচী নিশ্চিত করুন বাটনে চেপে আপনার সম্পূর্ণ এজেন্ডার একটি কপি আপনার ইমেইল ঠিকানায় পাঠানোর জন্যও বেছে নিতে পারেন।"
        }
      }
    ];

    // Russian tour steps
    const agendaStepsRussian = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Добро пожаловать в PTC Wizard!",
          description: "Потребуется около 30 секунд, чтобы узнать, почему мы — лучшее приложение для родительско-педагогических конференций.",
          imageComponent: TourListIcon,
          cta: "Начнём"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Просматривайте расписание как удобно",
          description:
            "После входа вы увидите страницу со списком запланированных встреч. Выберите вид: все встречи одним списком или по конференциям."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Запланируем встречу",
          description: "Найдите нужную конференцию и нажмите здесь, чтобы выбрать учителя и время встречи."
        }
      }
    ];

    const matrixStepsRussian = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Запись на встречи",
          description: "С PTC Wizard забронировать встречу проще простого: 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Начнём"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Шаг 1: Выберите дату",
          description: "Все даты конференции отображаются здесь. Нажмите на нужную."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Шаг 2: Выберите учителей",
          description:
            "Нажмите кнопку УЧИТЕЛЯ, чтобы увидеть всех доступных учителей и команды. Нажмите на учителя или учителей, чтобы отобразить их расписание.",
          error: "Выберите учителя"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Шаг 3: Выберите время",
          image: "/tour-matrix-4.png",
          description:
            "Здесь происходит магия. PTC Wizard показывает расписания выбранных учителей вместе — выберите удобное время."
        },
        options: { scrollToStep: { enabled: false } },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Проверьте повестку",
          image: "/tour-matrix-5.png",
          description:
            "Встреча бронируется сразу после выбора времени. Вы можете просмотреть все встречи, открыв панель повестки. Оттуда можно отправить копию на email, нажав «Подтвердить повестку»."
        }
      }
    ];

    // Ukrainian tour steps
    const agendaStepsUkrainian = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Ласкаво просимо до PTC Wizard!",
          description: "Потрібно лише близько 30 секунд, щоб дізнатися, чому ми — найкращий застосунок для батьківсько-педагогічних конференцій.",
          imageComponent: TourListIcon,
          cta: "Почнемо"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Переглядайте розклад як зручно",
          description:
            "Після входу ви побачите сторінку зі списком запланованих зустрічей. Оберіть: усі зустрічі одним списком або за конференціями."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Заплануймо зустріч",
          description: "Знайдіть потрібну конференцію і натисніть тут, щоб обрати вчителя та час зустрічі."
        }
      }
    ];

    const matrixStepsUkrainian = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Запис на зустрічі",
          description: "З PTC Wizard забронювати зустріч простіше простого: 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Почнемо"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Крок 1: Оберіть дату",
          description: "Усі дати конференції показані тут. Натисніть потрібну."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Крок 2: Оберіть вчителів",
          description:
            "Натисніть кнопку ВЧИТЕЛІ, щоб побачити всіх доступних вчителів та команди. Натисніть на вчителя, щоб показати їхній розклад.",
          error: "Оберіть вчителя"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Крок 3: Оберіть час",
          image: "/tour-matrix-4.png",
          description:
            "Ось де відбувається магія. PTC Wizard показує розклади обраних вчителів разом — оберіть зручний час."
        },
        options: { scrollToStep: { enabled: false } },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Перевірте порядок денний",
          image: "/tour-matrix-5.png",
          description:
            "Зустріч бронюється одразу після вибору часу. Ви можете переглянути всі зустрічі, відкривши панель порядку денного. Звідти можна надіслати копію на email, натиснувши «Підтвердити порядок денний»."
        }
      }
    ];

    // Uzbek tour steps
    const agendaStepsUzbek = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "PTC Wizard ga xush kelibsiz!",
          description: "Nima uchun biz eng yaxshi ota-ona-o'qituvchi konferensiya ilovasi ekanimizni o'rganish uchun atigi 30 soniya yetadi.",
          imageComponent: TourListIcon,
          cta: "Boshlaymiz"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Rejangizni xohlagancha ko'ring",
          description:
            "Tizimga kirganingizda, rejalashtirilgan uchrashuvlaringiz ro'yxatini ko'rasiz. Barcha uchrashuvlarni bitta ro'yxatda yoki konferensiya bo'yicha ko'ring."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Uchrashuvni rejalashtiramiz",
          description: "Kerakli konferensiyani toping va o'qituvchi hamda uchrashuv vaqtini tanlash uchun shu yerni bosing."
        }
      }
    ];

    const matrixStepsUzbek = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Uchrashuvlarga yoziling",
          description: "PTC Wizard bilan uchrashuvlarni bron qilish 1-2-3 kabi oson.",
          image: "/tour-matrix.svg",
          cta: "Boshlaymiz"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "1-qadam: Sanani tanlang",
          description: "Konferensiya sanalari shu yerda. Keraklisini bosing."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "2-qadam: O'qituvchilarni tanlang",
          description:
            "O'QITUVCHILAR tugmasini bosing va barcha mavjud o'qituvchilarni ko'ring. O'qituvchilarga bosing — ularning jadvali ko'rinadi.",
          error: "O'qituvchi tanlang"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "3-qadam: Vaqtni tanlang",
          image: "/tour-matrix-4.png",
          description:
            "Jodugar shu yerda ishlaydi. PTC Wizard tanlangan o'qituvchilar jadvallarini birga ko'rsatadi — o'zingizga qulay vaqtni tanlang."
        },
        options: { scrollToStep: { enabled: false } },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Kun tartibini ko'rib chiqing",
          image: "/tour-matrix-5.png",
          description:
            "Vaqtni tanlaganingizda uchrashuv bron qilinadi. Kun tartibi panelini ochib barcha uchrashuvlarni ko'rishingiz mumkin. Shuningdek, «Kun tartibini tasdiqlash» tugmasini bosib nusxani emailga yuborishingiz mumkin."
        }
      }
    ];

    // Georgian tour steps
    const agendaStepsGeorgian = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "კეთილი იყოს თქვენი მობრძანება PTC Wizard-ში!",
          description: "სჭირდება მხოლოდ 30 წამი, რომ გაიგოთ რატომ ვართ საუკეთესო მშობელ-მასწავლებელთა კონფერენციების აპლიკაცია.",
          imageComponent: TourListIcon,
          cta: "დავიწყოთ"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "ნახეთ თქვენი განრიგი სასურველი გზით",
          description:
            "შესვლის შემდეგ ნახავთ გვერდს თქვენი დაგეგმილი შეხვედრებით. აირჩიეთ ყველა შეხვედრის ნახვა ერთ სიაში ან კონფერენციების მიხედვით."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "დავпланиოთ შეხვედრა",
          description: "იპოვეთ სასურველი კონფერენცია და დააჭირეთ აქ მასწავლებლისა და დროის ასარჩევად."
        }
      }
    ];

    const matrixStepsGeorgian = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "დარეგისტრირდით შეხვედრებზე",
          description: "PTC Wizard-ით შეხვედრის დაჯავშნა ისეთივე მარტივია, როგორც 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "დავიწყოთ"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "ნაბიჯი 1: აირჩიეთ თარიღი",
          description: "კონფერენციის ყველა თარიღი აქ არის ნაჩვენები. დააჭირეთ სასურველს."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "ნაბიჯი 2: აირჩიეთ მასწავლებლები",
          description:
            "დააჭირეთ მასწავლებლები ღილაკს ყველა ხელმისაწვდომი მასწავლებლის სანახავად. დააჭირეთ მასწავლებელს განრიგის სანახავად.",
          error: "აირჩიეთ მასწავლებელი"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "ნაბიჯი 3: აირჩიეთ დრო",
          image: "/tour-matrix-4.png",
          description:
            "აი სად ხდება მაგია. PTC Wizard აჩვენებს ყველა არჩეული მასწავლებლის განრიგს ერთად — აირჩიეთ თქვენთვის მოსახერხებელი დრო."
        },
        options: { scrollToStep: { enabled: false } },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "შეამოწმეთ დღის წესრიგი",
          image: "/tour-matrix-5.png",
          description:
            "დროის არჩევისთანავე შეხვედრა ჯავშნილია. შეგიძლიათ ყველა შეხვედრის ნახვა დღის წესრიგის პანელის გახსნით. აქედან ასევე შეგიძლიათ გამოაგზავნოთ ასლი ელფოსტაზე «დღის წესრიგის დადასტურება» ღილაკით."
        }
      }
    ];

    // Cantonese tour steps
    const agendaStepsCantonese = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "歡迎使用 PTC Wizard！",
          description: "只需要大約30秒就可以了解點解我哋係史上最好嘅家長教師會議應用程式。",
          imageComponent: TourListIcon,
          cta: "開始啦"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "用你想嘅方式睇你嘅日程",
          description: "登入之後你會睇到列咗你已安排會議嘅頁面。揀一個列表睇晒所有會議，或者按會議分開睇。"
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "我哋嚟安排一次會議",
          description: "搵到你想要嘅會議，撳呢度開始揀教師同見面時間。"
        }
      }
    ];

    const matrixStepsCantonese = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "報名會議",
          description: "用 PTC Wizard，預約會議好似 1-2-3 咁簡單。",
          image: "/tour-matrix.svg",
          cta: "開始啦"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "步驟1：揀日期",
          description: "會議嘅所有日期都喺呢度。撳你想要嗰個就得。"
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "步驟2：揀你嘅教師",
          description:
            "撳教師掣睇該會議日所有可用嘅教師同隊伍。撳任何教師嚟顯示佢哋嘅時間表。",
          error: "揀一位教師"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "步驟3：揀時間段",
          image: "/tour-matrix-4.png",
          description:
            "呢度就係魔術發生嘅地方。PTC Wizard 會將你揀咗嘅所有教師時間表一齊顯示，你只需要揀啱你嘅時間段就得。"
        },
        options: { scrollToStep: { enabled: false } },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "睇你嘅議程",
          image: "/tour-matrix-5.png",
          description:
            "你一揀時間段會議就預約咗，但你可以隨時開議程抽屜睇晒所有會議。喺呢度你都可以撳確認時間表掣，將完整議程副本寄去你嘅電郵地址。"
        }
      }
    ];

    // Haitian Creole tour steps
    const agendaStepsHaitianCreole = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Byenveni nan PTC Wizard!",
          description: "Li pran sèlman anviwon 30 segonn pou aprann sa ki fè nou pi bon aplikasyon pou konferans paran-pwofesè tout tan.",
          imageComponent: TourListIcon,
          cta: "Ann kòmanse"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Gade orè ou jan ou vle",
          description:
            "Lè w konekte, w ap wè yon paj ki lis reinyon ou deja pwograme. Chwazi ant wè tout reinyon ou nan yon sèl lis, oswa separe pa konferans."
        }
      },
      {
        attachTo: { element: ".tab-content .active .btn-meetings" },
        content: {
          title: "Ann planifye yon reinyon",
          description: "Jis jwenn konferans ou vle epi peze isit la pou kòmanse chwazi yon pwofesè ak lè pou rankontre."
        }
      }
    ];

    const matrixStepsHaitianCreole = [
      {
        attachTo: { element: ".navbar" },
        content: {
          title: "Enskri pou reinyon",
          description: "Avèk PTC Wizard, rezève reinyon fasil tankou 1-2-3.",
          image: "/tour-matrix.svg",
          cta: "Ann fè li"
        },
        center: true
      },
      {
        attachTo: { element: "#pills-tab" },
        content: {
          title: "Etap 1: Chwazi dat la",
          description: "Tout dat konferans lan montre isit la. Jis peze yon sèl ou vle."
        }
      },
      {
        attachTo: { element: "#BtnDrawerTeachers" },
        content: {
          title: "Etap 2: Chwazi pwofesè ou",
          description:
            "Peze bouton PWOFESÈ pou wè tout pwofesè ekip ki disponib pou jou konferans sa a. Peze sou nenpòt pwofesè pou montre orè yo.",
          error: "Chwazi yon pwofesè"
        }
      },
      {
        attachTo: { element: ".schedule-matrix" },
        content: {
          title: "Etap 3: Chwazi yon kren lè",
          image: "/tour-matrix-4.png",
          description:
            "Se la maji rive. PTC Wizard montre w tout orè pwofesè ou chwazi ansanm, epi tout sa w dwe fè se chwazi kren lè ki travay pou ou."
        },
        options: { scrollToStep: { enabled: false } },
        center: true,
        class: "primary-25"
      },
      {
        attachTo: { element: "#BtnDrawerAgenda" },
        content: {
          title: "Revize aganda ou",
          image: "/tour-matrix-5.png",
          description:
            "Reinyon ou an rezève dèske w chwazi yon kren lè, men w ka revize tout reinyon ou nenpòt ki lè lè w louvri tiwa aganda. Soti isit la w ka tou chwazi voye yon kopi aganda konplè ou nan adrès imèl ou lè w peze bouton Konfime Orè."
        }
      }
    ];

    const handleExit = () => {
      // Use the finish function from useVOnboarding instead of the slot's exit
      finish();
      emit('tour-closed');
    };

    return {
      wrapper,
      steps,
      agendaSteps,
      agendaStepsSpanish,
      agendaStepsFrench,
      agendaStepsGerman,
      agendaStepsArabic,
      agendaStepsChinese,
      agendaStepsJapanese,
      agendaStepsBengali,
      agendaStepsRussian,
      agendaStepsUkrainian,
      agendaStepsUzbek,
      agendaStepsGeorgian,
      agendaStepsCantonese,
      agendaStepsHaitianCreole,
      matrixSteps,
      matrixStepsSpanish,
      matrixStepsFrench,
      matrixStepsGerman,
      matrixStepsArabic,
      matrixStepsChinese,
      matrixStepsJapanese,
      matrixStepsBengali,
      matrixStepsRussian,
      matrixStepsUkrainian,
      matrixStepsUzbek,
      matrixStepsGeorgian,
      matrixStepsCantonese,
      matrixStepsHaitianCreole,
      start,
      finish,
      goToStep,
      handleExit
    };
  }
});
</script>
